"use client";

import React from "react";
import { Clock, PhoneCall, ShieldAlert, Wrench, Droplets, Flame, CircleCheck as CheckCircle2, ArrowRight, Zap } from 'lucide-react';
import { motion, useReducedMotion } from "framer-motion";
import { Link } from "@/components/common/Link";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from "@/components/ui/card";
const emergencyFeatures = [{
  title: "30-45 Min Response Time",
  description: "Our mobile plumbing units are stationed across Jonesboro and Clayton County for immediate emergency dispatch.",
  icon: Zap,
  highlight: "Rapid Dispatch"
}, {
  title: "true 24/7/365 Coverage",
  description: "Burst pipes and sewer overflows do not wait for business hours. We answer live calls day, night, weekends, and holidays.",
  icon: Clock,
  highlight: "Zero Voicemail"
}, {
  title: "Licensed & Equipped Techs",
  description: "Every emergency van arrives fully stocked with industrial tools, cameras, and replacement parts to fix problems on the first visit.",
  icon: ShieldAlert,
  highlight: "First-Trip Fix"
}];
const priorityServices = [{
  title: "Burst & Leaking Pipe Repair",
  desc: "Immediate shutoff assistance, pipe line isolation, and emergency structural leak repairs.",
  icon: Droplets,
  badge: "Immediate Threat"
}, {
  title: "Emergency Drain & Sewer Clearing",
  desc: "Heavy-duty hydro-jetting and motorized augers to clear dangerous back-ups and flooded drains.",
  icon: Wrench,
  badge: "Same-Day Service"
}, {
  title: "Water Heater Breakdown Repairs",
  desc: "Rapid diagnosis and repair for leaking, smoking, or failed residential and commercial water heaters.",
  icon: Flame,
  badge: "Hot Water Restored"
}];
const guaranteePoints = ["No overtime surge charges for midnight calls", "Upfront pricing before any emergency work begins", "Fully licensed, bonded, and insured in Georgia", "Clayton County local dispatch and familiar technicians"];
export default function Overview() {
  const shouldReduceMotion = useReducedMotion();
  return <section id="emergencyservicesoverview" data-nav="light" className="bg-background text-foreground py-20 lg:py-28 relative overflow-hidden">
      <div className="container mx-auto px-4 sm:px-6 lg:px-8">
        {/* Top Header / Urgency Banner */}
        <div className="max-w-3xl mx-auto text-center mb-16">
          <motion.div initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 15
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.4
        }} className="flex justify-center mb-4">
            <Badge variant="secondary" className="px-3.5 py-1 text-xs sm:text-sm font-semibold rounded-full border border-border">
              <span className="inline-block size-2 rounded-full bg-destructive animate-pulse mr-1" />
              Jonesboro Live Emergency Response
            </Badge>
          </motion.div>

          <motion.h2 initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.1
        }} className="text-3xl sm:text-4xl lg:text-5xl font-default font-bold tracking-tight text-foreground">
            Immediate 24/7 Plumbing Services When Every Second Counts
          </motion.h2>

          <motion.p initial={shouldReduceMotion ? {
          opacity: 1
        } : {
          opacity: 0,
          y: 20
        }} whileInView={{
          opacity: 1,
          y: 0
        }} viewport={{
          once: true
        }} transition={{
          duration: 0.5,
          delay: 0.2
        }} className="mt-5 text-base sm:text-lg text-muted-foreground leading-relaxed">
            Water damage multiplies by the minute. Our Jonesboro emergency plumbing crews arrive equipped to stop the leak, restore drainage, and safeguard your home without delay.
          </motion.p>
        </div>

        {/* Feature Cards Grid */}
        <div className="grid grid-cols-1 md:grid-cols-3 gap-6 lg:gap-8 mb-16">
          {emergencyFeatures.map((feature, i) => {
          const IconComponent = feature.icon;
          return <motion.div key={feature.title} data-index={i} initial={shouldReduceMotion ? {
            opacity: 1
          } : {
            opacity: 0,
            y: 25
          }} whileInView={{
            opacity: 1,
            y: 0
          }} viewport={{
            once: true
          }} transition={{
            duration: 0.4,
            delay: i * 0.1
          }} className="h-full">
                <Card className="h-full border border-border bg-card shadow-sm hover:shadow-md transition-shadow">
                  <CardHeader className="pb-4">
                    <div className="flex items-center justify-between mb-4">
                      <div className="p-3 rounded-xl bg-primary text-primary-foreground">
                        <IconComponent className="size-6" />
                      </div>
                      <Badge variant="outline" className="text-xs font-medium">
                        {feature.highlight}
                      </Badge>
                    </div>
                    <CardTitle className="text-xl font-default font-bold text-card-foreground">
                      {feature.title}
                    </CardTitle>
                  </CardHeader>
                  <CardContent>
                    <CardDescription className="text-muted-foreground text-sm sm:text-base leading-normal">
                      {feature.description}
                    </CardDescription>
                  </CardContent>
                </Card>
              </motion.div>;
        })}
        </div>

        {/* Urgent Service Callout Box with Services breakdown */}
        <div className="bg-muted/50 border border-border rounded-2xl p-6 sm:p-8 lg:p-10 mb-16">
          <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-center">
            <div className="lg:col-span-5">
              <Badge variant="default" className="mb-3 text-xs uppercase tracking-wider font-semibold">
                High Priority Emergencies
              </Badge>
              <h3 className="text-2xl sm:text-3xl font-default font-bold text-foreground">
                Common Urgent Plumbing Crises We Solve Daily
              </h3>
              <p className="mt-3 text-muted-foreground text-sm sm:text-base leading-relaxed">
                Whether it is 2:00 PM or 2:00 AM, our dispatch center handles dangerous backups, flood threats, and catastrophic component failures across Jonesboro.
              </p>

              <ul className="mt-6 space-y-3">
                {guaranteePoints.map((point, i) => <li key={i} data-index={i} className="flex items-start gap-2.5 text-sm text-foreground">
                    <CheckCircle2 className="size-5 text-accent shrink-0 mt-0.5" />
                    <span>{point}</span>
                  </li>)}
              </ul>

              <div className="mt-8 flex flex-wrap gap-4 items-center">
                <Button asChild size="lg" className="bg-primary text-primary-foreground hover:bg-primary/90 font-semibold shadow-md">
                  <a href="tel:4708479076" className="flex items-center gap-2">
                    <PhoneCall className="size-5 animate-pulse" />
                    <span>Call Emergency Line</span>
                  </a>
                </Button>
                <Button asChild variant="outline" size="lg" className="border-border">
                  <Link to="/problems/" className="flex items-center gap-2">
                    <span>View Common Problems</span>
                    <ArrowRight className="size-4" />
                  </Link>
                </Button>
              </div>
            </div>

            <div className="lg:col-span-7 grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-1 gap-4">
              {priorityServices.map((service, i) => {
              const ServiceIcon = service.icon;
              return <div key={service.title} data-index={i} className="p-5 rounded-xl bg-card border border-border shadow-xs hover:border-primary/40 transition-colors flex flex-col sm:flex-row gap-4 items-start">
                    <div className="p-2.5 rounded-lg bg-secondary text-secondary-foreground shrink-0 mt-1">
                      <ServiceIcon className="size-5" />
                    </div>
                    <div className="flex-1">
                      <div className="flex flex-wrap items-center justify-between gap-2 mb-1">
                        <h4 className="font-default font-semibold text-card-foreground text-base">
                          {service.title}
                        </h4>
                        <span className="text-xs px-2 py-0.5 rounded-md bg-secondary text-secondary-foreground font-medium">
                          {service.badge}
                        </span>
                      </div>
                      <p className="text-xs sm:text-sm text-muted-foreground leading-relaxed">
                        {service.desc}
                      </p>
                    </div>
                  </div>;
            })}
            </div>
          </div>
        </div>

        {/* Action strip */}
        <div className="flex flex-col sm:flex-row items-center justify-between gap-6 p-6 rounded-xl bg-primary text-primary-foreground">
          <div className="flex items-center gap-4 text-center sm:text-left">
            <div className="p-3 bg-primary-foreground/10 rounded-full hidden sm:block">
              <Clock className="size-6 text-accent" />
            </div>
            <div>
              <p className="font-semibold text-lg">Need Immediate On-Site Assessment?</p>
              <p className="text-sm text-primary-foreground/80">Plumbers on standby in Jonesboro, GA right now.</p>
            </div>
          </div>
          <div className="flex items-center gap-3 w-full sm:w-auto justify-end">
            <Button asChild variant="secondary" className="w-full sm:w-auto font-medium">
              <Link to="/services/">
                Explore All Services
              </Link>
            </Button>
            <Button asChild className="w-full sm:w-auto bg-accent text-accent-foreground hover:bg-accent/90 font-bold shadow-sm">
              <Link to="/contact/">
                Request Service
              </Link>
            </Button>
          </div>
        </div>
      </div>
    </section>;
}